import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { PostExtraCode } from 'types'
import { Subscription } from 'rxjs'
import { BasePostForm } from './base-post-form.component'
import { Subject } from 'rxjs'
import { PostActions } from 'actions/post-actions'
import {
  fullDayWithTime,
  hasPlusVideo,
  planChangeSummaryPath,
  videoPrice,
  quantityChargeDollars,
} from 'utils'
import { centsToCurrencyString, paymentIcon } from 'logic/pricing'
import { availableCredits } from 'logic/subscription-states'
import { Post } from 'src/modules/submissions/submission.queries'
import { get } from 'lodash-es'
import {
  PostCreditPayment,
  PaymentOption,
  PurchasePostFormPaymentOptionType,
} from 'src/interfaces/post-credit-payment.interface'
import { MatDialog } from '@angular/material/dialog'
import { SaveKeywordDialog } from 'src/modules/keywords/save-keyword-dialog.component'
import { UserMutations } from '../../../graphql/mutations/user.mutations'
import { DEFAULT_DIALOG_OPTIONS } from 'src/modules/shared/constants/default-dialog-options'
import sweetAlert from 'src/bmAlert'
import { KeywordMutations } from 'src/graphql/mutations/keyword.mutations'
import { DeclineSubmissionsHelper } from 'src/modules/keywords/helpers/decline-submissions-alerts.helper'

@Component({
  selector: 'purchase-post-form',
  styles: [
    `
      label {
        font-weight: normal;
      }
      .radio-indent {
        margin-left: 17px;
      }
      .payment-option-text {
        min-width: 150px;
        text-align: left;
      }
    `,
  ],
  template: `
    <h4 class="text-green">Accept this submission</h4>
    <form *ngIf="ready()" (ngSubmit)="onSubmit()">
      <div *ngIf="showVideoExtra" class="checkbox biggish-text small-top-buffer">
        <hr />
        <label>
          <input
            type="checkbox"
            [checked]="selectedExtras.includes('video')"
            (change)="toggleExtra('video', $event.target.checked)" />
          <i class="fas fa-video ml-1 mr-1"></i>
          Add a video for only <strong>{{ prettyPrice(videoPrice) }}</strong>
          <a class="ml-2" routerLink="." (click)="videoInfoModalVisible = true">Learn more</a>
        </label>
        <div class="ml-3 small">
          <h6 class="highlight d-inline">New!</h6>
          <i class="ml-2"
            ><strong>Save $10 per video</strong> by adding +Video to your subscription!
          </i>
          <a [href]="plusVideoPlanChange">Update my subscription</a>
        </div>
        <hr />
      </div>

      <div>
        <h4 class="mb-3">Leave a review</h4>
        <textarea
          class="form-control textarea-mobile"
          rows="2"
          (focus)="$event.target.rows = 6"
          name="appReview"
          [(ngModel)]="appReview"
          placeholder="Let the writer know what you liked and what they could maybe improve on next time around.">
        </textarea>
        <rating-stars
          class="mt-2"
          [suppressMobile]="true"
          [value]="rating"
          (onChange)="rating = $event"></rating-stars>
      </div>

      <div *ngIf="reservedByPostCredit" class="mt-3">
        <div *ngIf="post.keyword.reservedPostCreditsCount === 1">
          This submission will fulfill your request.
        </div>
        <div *ngIf="post.keyword.reservedPostCreditsCount > 1">
          This submission will fulfill one of the {{ post.keyword.reservedPostCreditsCount }} you
          still need for this request.
        </div>
      </div>

      <div>
        <div *ngIf="!reservedByPostCredit">
          <div class="my-3">This piece hasn't been paid for yet. Would you like to keep it?</div>
          <h5>Yes please! I'll pay with</h5>
        </div>

        <div class="mt-2 d-flex flex-row justify-content-between align-items-center">
          <div class="col d-flex flex-row p-0">
            <div
              *ngIf="!reservedByPostCredit"
              dropdown
              class="btn-group mr-1 purchase-post-form-dropdown-button-group"
              placement="bottom right">
              <button
                dropdownToggle
                type="button"
                class="btn btn-outline-tertiary dropdown-toggle d-flex justify-content-between align-items-center bg-white">
                <span *ngIf="!selectedPaymentOption" class="payment-option-text">
                  <i class="fa fa-exclamation-circle text-danger mr-1"></i>
                  make a selection
                </span>
                <span *ngIf="selectedPaymentOption" class="payment-option-text">{{
                  selectedPaymentOption.optionPlaceholder
                }}</span>
              </button>
              <div *dropdownMenu class="dropdown-menu mb-3" role="menu">
                <a
                  *ngFor="let option of paymentOptions"
                  [class.disabled]="option.optionDisabled"
                  (click)="selectPaymentOption(option.optionType)"
                  class="dropdown-item cursor-pointer">
                  <strong>{{ option.optionText }}</strong
                  ><br />
                  <small>
                    <i *ngIf="option.optionType === 'charge'" class="{{ paymentIcon() }}"> </i>
                    {{ option.optionDescription }}
                  </small>
                </a>
              </div>
            </div>
            <div>
              <button
                type="submit"
                class="btn btn-primary"
                [disabled]="submitted || !this.selectedPaymentOption">
                <span *ngIf="!submitted">Accept this piece</span>
                <span *ngIf="submitted">Purchasing...</span>
              </button>
            </div>
          </div>

          <div class="col-auto p-0">
            <button type="button" class="btn btn-default" (click)="onClose()">Cancel</button>
          </div>
        </div>
      </div>

      <div class="mt-2 small italics">
        This piece will be sent to you as specified in your
        <a href="/dashboard/{{ customer.id }}/delivery_options" target="_blank">delivery options</a>
      </div>
    </form>
    <rate-post-modal
      *ngIf="ratePostModalVisible && allowRatePostModal()"
      [post]="post"
      action="purchase"
      (done)="postPurchaseSubject.next(post); postPurchaseSubject.complete()"></rate-post-modal>
    <video-purchase-modal
      *ngIf="videoPurchaseModalVisible"
      [step]="'survey'"
      [price]="prettyPrice(videoPrice)"
      (close)="postPurchaseSubject.next(post); postPurchaseSubject.complete()"
      [businessName]="customer?.businessName"
      [postTitle]="post?.title"></video-purchase-modal>
    <video-info-modal
      *ngIf="videoInfoModalVisible"
      (close)="videoInfoModalVisible = false"
      [price]="prettyPrice(videoPrice)"
      [customer]="customer"></video-info-modal>
  `,
})
export class PurchasePostForm
  extends BasePostForm
  implements OnInit, PostCreditPayment<PurchasePostFormPaymentOptionType>
{
  availableExtras: PostExtraCode[] = ['video']
  selectedExtras: 'video'[] = []
  ratePostModalVisible: boolean = false
  videoPurchaseModalVisible: boolean = false
  videoInfoModalVisible: boolean = false
  postPurchaseSubject: Subject<Post> = new Subject<Post>()
  appReview: string
  rating: number
  paymentOptions: PaymentOption<PurchasePostFormPaymentOptionType>[]
  selectedPaymentOption: PaymentOption<PurchasePostFormPaymentOptionType>
  reminderBetterSubmissions: boolean = true
  dialogSub: Subscription

  constructor(
    private postActions: PostActions,
    public dialog: MatDialog,
    private changeDetector: ChangeDetectorRef,
    private userMutations: UserMutations,
    private keywordMutations: KeywordMutations
  ) {
    super()
  }

  ngOnInit(): void {
    this.appReview = this.post.appPostReview?.content
    this.rating = this.post.appRating?.value
    this.reminderBetterSubmissions = this.currentUser?.uiPreferences.reminderBetterSubmissions
  }

  ngOnChanges() {
    if (this.ready()) {
      if (this.reservedByPostCredit) {
        this.selectedPaymentOption = { optionType: 'reserved_credit' }
      } else {
        this.updatePaymentOptions()

        if (this.optionValidForSelection('unreserved_credit')) {
          this.selectPaymentOption('unreserved_credit')
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.dialogSub) this.dialogSub.unsubscribe()
  }

  ready(): boolean {
    return !!(this.customer && this.customer.subscription && this.customer.subscription.plan)
  }

  get reservedByPostCredit(): boolean {
    return this.post?.keyword?.reservedPostCreditsCount > 0
  }

  get hasCredits(): boolean {
    return this.customer.postCreditsCount > 0
  }

  get showVideoPurchaseModal(): boolean {
    return this.selectedExtras.length > 0 || (this.customer && hasPlusVideo(this.customer))
  }

  get videoPrice(): number {
    return videoPrice(this.customer)
  }

  get showVideoExtra(): boolean {
    return this.customer && !hasPlusVideo(this.customer) && this.availableExtras.includes('video')
  }

  get plusVideoPlanChange(): string {
    return planChangeSummaryPath({
      customer: this.customer,
      options: [...this.customer.subscription.planAddOns.map((pao) => pao.code), 'option-h'],
    })
  }

  get canDeclineRemainingPendingSubmissions(): boolean {
    return this.post.keyword.reservedPostCreditsCount === 1 && this.post.keyword.queuedCount > 1
  }

  updatePaymentOptions(): void {
    const { extraPostPrice } = this.customer.subscription
    const { billingType, lastFour } = this.customer.account
    const paypal = billingType === 'paypal'

    this.paymentOptions = [
      {
        optionType: 'unreserved_credit',
        optionDisabled: false,
        optionText: `Pay 1 credit`,
        optionDescription: this.hasCredits
          ? 'Save 5% by using subscription credits'
          : 'Not enough credits for this method',
        optionPlaceholder: `1 credit`,
        confirmMessage: ``,
      },
      {
        optionType: 'charge',
        optionDisabled: false,
        optionText: 'Pay ' + quantityChargeDollars(1, extraPostPrice),
        optionDescription: paypal
          ? `One-time charge to linked PayPal account`
          : `One-time charge to card ending in ${lastFour}`,
        optionPlaceholder: quantityChargeDollars(1, extraPostPrice),
        confirmMessage:
          (paypal
            ? `Charge ${quantityChargeDollars(
                1,
                extraPostPrice
              )} to linked <i class="${this.paymentIcon()}"></i> <b>PayPal</b> account. `
            : `Charge ${quantityChargeDollars(
                1,
                extraPostPrice
              )} to <i class="${this.paymentIcon()}"></i> ending in <b>${lastFour}</b>. `) +
          'Any refund will appear as credits.',
      },
    ]

    this.paymentOptions.forEach(
      (o) => (o.optionDisabled = !this.optionValidForSelection(o.optionType))
    )
  }

  optionValidForSelection(selectedOptionType: PurchasePostFormPaymentOptionType): boolean {
    const { reservedPostCreditsCount } = this.post.keyword
    const numCredits = availableCredits(this.customer)

    switch (selectedOptionType) {
      case 'charge':
        return reservedPostCreditsCount === 0
      case 'unreserved_credit':
        return numCredits > 0
      case 'reserved_credit':
        return reservedPostCreditsCount > 0
    }
  }

  selectPaymentOption(selectedOptionType: PurchasePostFormPaymentOptionType): void {
    if (!this.optionValidForSelection(selectedOptionType)) {
      this.selectedPaymentOption = null
      return
    }

    this.selectedPaymentOption = this.paymentOptions.find((o) => o.optionType == selectedOptionType)
  }

  paymentIcon(): string {
    return paymentIcon(this.customer.account)
  }

  onSubmit(): void {
    if (!this.submitted && this.selectedPaymentOption) {
      this.submitted = true
      const onError = () =>
        (this.submitted = this.ratePostModalVisible = this.videoPurchaseModalVisible = false)

      // Needed to populate the rating modal stars
      if (this.rating) this.post.appRating = { ...this.post.appRating, value: this.rating }

      if (this.showVideoPurchaseModal) {
        this.videoPurchaseModalVisible = true
        this.postActions
          .postNow(
            this.customer.id,
            this.post.id,
            this.selectedPaymentOption.optionType,
            this.selectedExtras,
            this.appReview,
            this.rating,
            this.postPurchaseSubject.toPromise()
          )
          .then(() => this.handlePostNowSuccess())
          .catch(onError)
      } else if (
        this.allowRatePostModal() &&
        !(this.selectedPaymentOption.optionType === 'charge')
      ) {
        this.ratePostModalVisible = true
        this.postActions
          .postNow(
            this.customer.id,
            this.post.id,
            this.selectedPaymentOption.optionType,
            this.selectedExtras,
            this.appReview,
            this.rating,
            this.postPurchaseSubject.toPromise()
          )
          .then(() => this.handlePostNowSuccess())
          .catch(onError)
      } else if (this.selectedPaymentOption.optionType === 'charge') {
        this.openDialogForCharges()
      } else {
        this.postActions
          .postNow(
            this.customer.id,
            this.post.id,
            this.selectedPaymentOption.optionType,
            this.selectedExtras,
            this.appReview,
            this.rating
          )
          .then(() => this.handlePostNowSuccess())
          .catch(onError)
      }
    }
  }

  openDialogForCharges(): void {
    const onError = () => {
      this.submitted = false
    }

    let dialogRef = this.dialog.open(SaveKeywordDialog, {
      ...DEFAULT_DIALOG_OPTIONS,
      data: {
        dontShowAgain: !this.reminderBetterSubmissions,
        title: 'Please confirm to purchase content',
        message: this.selectedPaymentOption.confirmMessage,
        newRequest: false,
        buttonText: 'Charge',
      },
    })

    this.dialogSub = dialogRef.afterClosed().subscribe((closedFromButton) => {
      if (closedFromButton && (closedFromButton.submit || closedFromButton.dontShowAgain)) {
        this.reminderBetterSubmissions = !closedFromButton.dontShowAgain
        if (!this.reminderBetterSubmissions) {
          this.userMutations.updateUserPreferences({
            reminderBetterSubmissions: this.reminderBetterSubmissions,
          })
        }
        if (closedFromButton.submit) {
          this.postActions
            .postNow(
              this.customer.id,
              this.post.id,
              this.selectedPaymentOption.optionType,
              this.selectedExtras,
              this.appReview,
              this.rating
            )
            .then(() => this.handlePostNowSuccess())
            .catch(onError)
        }
      }
      this.submitted = false
      this.changeDetector.detectChanges()
    })
  }

  formatted(date: Date): string {
    return fullDayWithTime(date)
  }

  allowRatePostModal(): boolean {
    return !this.rating || (this.rating >= 4 && !this.post.writerPreferred)
  }

  toggleExtra(extraCode: PostExtraCode, include: boolean = null): false {
    include = include || !this.selectedExtras.includes(extraCode)
    if (include && !this.selectedExtras.includes(extraCode)) this.selectedExtras.push(extraCode)
    else if (!include && this.selectedExtras.includes(extraCode))
      this.selectedExtras = this.selectedExtras.filter((c) => c !== extraCode)
    return false
  }

  prettyPrice(cents: number): string {
    return centsToCurrencyString(cents, { truncateZeroCents: true })
  }

  onPostNowSuccess(): void {
    this.submitted = false
    this.onClose()
    this.deprecatedStoreChange.emit(null)
  }

  async declineRemainingKeywordSubmissions(): Promise<void> {
    const confirmDeclineSubmissions =
      await DeclineSubmissionsHelper.showDeclineInactiveSubmissionsSweetAlert(
        this.post.keyword.queuedCount - 1
      )
    if (confirmDeclineSubmissions)
      await this.keywordMutations.declineRemainingKeywordSubmissions({
        id: this.post.keyword.id,
      })
  }

  handlePostNowSuccess(): void {
    if (this.canDeclineRemainingPendingSubmissions) this.declineRemainingKeywordSubmissions()
    this.onPostNowSuccess()
  }
}
